import { useState, useEffect, useMemo, useRef, useDeferredValue } from "react";
import Avatar from "@mui/material/Avatar";
import { deepOrange } from "@mui/material/colors";
import { FixedSizeList as List } from "react-window";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { FiMoreVertical } from "react-icons/fi";
import { FaEnvelopeOpenText } from "react-icons/fa";
import useAxiosInstance from "../../helpers/useAxiosInstance";
import { getDisplayName } from "../../utils/message.utils";
import { useDebounce } from "../custom/useDebounce";

const ROW_HEIGHT = 64;
const SKELETON_COUNT = 8;

const ContactsList = ({ onSelect, selectedContact, selectedThreadId }) => {
  const [threads, setThreads] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [errorOccurred, setErrorOccurred] = useState(false);

  const listRef = useRef();
  const navigate = useNavigate();
  const axiosInstance = useAxiosInstance();

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setInitialLoading(true);
      if (selectedThreadId) {
        const success = await loadInitialThread(selectedThreadId);
        if (!success && !cancelled) {
          await fetchThreads(null, false);
        }
      } else {
        await fetchThreads(null, false);
      }
      setInitialLoading(false);
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [selectedThreadId]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (deferredSearch) {
        fetchThreads(null, false, deferredSearch);
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [deferredSearch]);

  const loadInitialThread = async (thread_id) => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/threads/one/${thread_id}`
      );
      const thread = res?.data?.data;
      if (thread) {
        setThreads([thread]);
        onSelect(thread);
        setHasMore(false);
        return true;
      }
    } catch (err) {
      console.error("Failed to fetch specific thread", err);
    }
    return false;
  };

  const fetchThreads = async (
    cursorValue = null,
    append = false,
    search = ""
  ) => {
    if ((append && !hasMore) || isLoading) return;
    if(!append) setInitialLoading(true)
    setIsLoading(true);
    setErrorOccurred(false);

    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/threads/list`,
        {
          params: {
            cursor: cursorValue,
            limit: 20,
            search,
          },
        }
      );

      const newThreads = res.data?.data ?? [];
      const newCursor = res.data?.pagination?.nextCursor ?? null;
      const more = res.data?.pagination?.hasMore ?? false;

      setThreads((prev) => (append ? [...prev, ...newThreads] : newThreads));
      setCursor(newCursor);
      setHasMore(more);

      if (!selectedContact && newThreads.length > 0) {
        onSelect(newThreads[0]);
      }
    } catch (err) {
      console.error("Failed to fetch threads", err);
      setErrorOccurred(true);
    } finally {
      setIsLoading(false);
      if(!append) setInitialLoading(false)
    }
  };

  const debouncedFetch = useDebounce(fetchThreads, 1000);

  const handleSearchText = (val) => {
    setSearchTerm(val)
    debouncedFetch(null, false, val)
  };
  
  const handleRetry = () => {
    fetchThreads(null, false, deferredSearch);
  };

  const handleScroll = ({ scrollOffset, scrollDirection }) => {
    const listElement = listRef?.current?._outerRef;
    if (!listElement) return;

    const bottom =
      listElement.scrollHeight - listElement.scrollTop <=
      listElement.clientHeight + ROW_HEIGHT * 2;

    if (bottom && !isLoading && hasMore) {
      fetchThreads(cursor, true, deferredSearch);
    }
  };

  // const filteredContacts = useMemo(() => {
  //   const term = deferredSearch.trim().toLowerCase();
  //   if (!term) return threads;
  //   return threads.filter(({ phone_number, client }) => {
  //     const identifier = (
  //       (client && client?.firstname) ?? phone_number ?? ""
  //     ).toLowerCase();
  //     return identifier.includes(term);
  //   });
  // }, [threads, deferredSearch]);

  const Row = ({ index, style }) => {
    const {
      client = { firstname: "" },
      contact = { firstName: "" },
      participants,
      thread_id,
    } = threads[index];
    const name = contact?.firstName ?? client?.firstname;
    const isSelected = selectedContact?.thread_id === thread_id;
    const dropdownRef = useRef(null);

    const handleMoreClick = (e) => {
      e.stopPropagation();
      setMenuOpenIndex(menuOpenIndex === index ? null : index);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          menuOpenIndex === index &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setMenuOpenIndex(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpenIndex, index]);

    return (
      <li
        style={style}
        key={thread_id}
        onClick={() => onSelect(threads[index])}
        className={`flex items-center justify-between px-4 border-b py-3 cursor-pointer transition-colors duration-150 ${
          isSelected ? "bg-blue-400 font-medium" : "hover:bg-gray-300 bg-white"
        }`}
      >
        <div className="flex items-center gap-3 overflow-hidden">
          {name ? (
            <Avatar sx={{ bgcolor: deepOrange[500], width: 32, height: 32 }}>
              {name.charAt(0)}
            </Avatar>
          ) : (
            <Avatar src="user.png" sx={{ width: 32, height: 32 }} />
          )}
          <span className="truncate">{getDisplayName(client,participants,contact)}</span>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            className="hover:bg-gray-200 p-1 rounded"
            onClick={handleMoreClick}
          >
            <FiMoreVertical className="text-gray-600" />
          </button>

          {/* {menuOpenIndex === index && (
            <ul className="absolute right-0 top-8 w-48 bg-white border shadow-lg rounded-md z-50 text-sm">
              <li
                className="flex items-center gap-2 px-4 py-2 text-gray-400 cursor-not-allowed"
                title="Not Allowed"
              >
                <FaEnvelopeOpenText className="text-blue-500" />
                Ignore
              </li>
            </ul>
          )} */}
        </div>
      </li>
    );
  };

  const SkeletonRow = ({ style }) => (
    <li
      style={style}
      className="flex items-center justify-between px-4 border-b py-3 animate-pulse"
    >
      <div className="flex items-center gap-3 w-full">
        <div className="bg-gray-300 rounded-full h-8 w-8" />
        <div className="bg-gray-300 h-4 w-3/4 rounded" />
      </div>
    </li>
  );

  return (
    <div className="w-[350px] bg-gray-100 border-r overflow-hidden flex flex-col">
      <div className="p-4 border-b bg-white border-gray-200">
        <div className="mb-2 w-fit">
          <button
            onClick={() => navigate(-1)}
            className="group flex items-center bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded transition-all duration-300 overflow-hidden"
          >
            <ChevronLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="ml-2 transition-all group-hover:max-w-xs group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-0 max-w-0 opacity-0 scale-95 translate-x-2 whitespace-nowrap overflow-hidden">
              Back to Conversations
            </span>
          </button>
        </div>

        <div className="flex mb-2 items-center justify-between">
          <span className="text-lg font-semibold">Contacts</span>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name or phone number..."
            //disabled={true}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={searchTerm}
            onChange={(e)=>{
              handleSearchText(e.target.value ?? '')
            }}
          />
          {searchTerm && (
              <button
                onClick={() => {
                  handleSearchText('')
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            )}
        </div>
      </div>

      {errorOccurred ? (
        <div className="flex flex-col gap-2 p-4 items-center justify-center text-red-500">
          <div>Failed to load contacts.</div>
          <button
            onClick={handleRetry}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      ) : initialLoading ? (
        <List
          height={window.innerHeight - 180}
          itemCount={SKELETON_COUNT}
          itemSize={ROW_HEIGHT}
          width={"100%"}
        >
          {SkeletonRow}
        </List>
      ) : threads.length === 0 ? (
        <div className="text-gray-500 p-4 text-center">No contacts found.</div>
      ) : (
        <>
          <List
            height={window.innerHeight - 180}
            itemCount={threads.length}
            itemSize={ROW_HEIGHT}
            width={"100%"}
            ref={listRef}
            onScroll={handleScroll}
          >
            {Row}
          </List>

          {isLoading && (
            <div className="text-center py-3 text-sm text-gray-400">
              Loading more...
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactsList;


